import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { Badge } from "../badge";
import { formatCalendarDate, calendarDayAnchor } from "../../utils/dates";
import { ACCENT, formatMXN } from "./shared";
import {
  asientosDelLedger,
  movimientosDeCaja,
  ETIQUETAS_ASIENTO,
} from "../../utils/ledger";

// Cuánto crece la lista con cada "Ver más".
const PAGINA = 50;

// Un movimiento por renglón, del más reciente al más viejo: es el estado de
// cuenta del venue, y solo lista dinero que DE VERDAD se movió (FRA-345).
// Los costos por pagar viven en Por pagar y entran aquí el día que se pagan,
// con su fecha de pago. Los depósitos en garantía —de clientes y de
// proveedores— sí aparecen: mover $40,000 a tu cuenta es exactamente lo que
// un estado de cuenta tiene que mostrar. El consumo imputado de inventario
// y las aplicaciones de depósito no: reclasifican, no mueven caja.
const CLASES = [
  { id: "todos", label: "Todo" },
  { id: "pago", label: "Pagos" },
  { id: "reembolso", label: "Reembolsos" },
  { id: "gasto", label: "Gastos" },
  { id: "deposito", label: "Depósitos" },
  { id: "retiro", label: "Retiros" },
];

const BADGE = {
  pago: { color: "emerald" },
  reembolso: { color: "red" },
  gasto: { color: "amber" },
  comision_planner: { color: "indigo" },
  retiro: { color: "zinc" },
  deposito_recibido: { color: "blue" },
  deposito_devuelto: { color: "purple" },
  devolucion_proveedor: { color: "teal" },
};

const CuentasMovimientos = () => {
  const navigate = useNavigate();
  const {
    payments,
    paymentsLoading,
    refunds,
    refundsLoading,
    costos,
    costosLoading,
    retiros,
    retirosLoading,
    clienteDepositos,
    clienteDepositosLoading,
    loadProveedorDepositos,
    proveedoresLoading,
    events,
    calendarTimezone,
  } = useVenue();

  const [clase, setClase] = useState("todos");
  const [limite, setLimite] = useState(PAGINA);

  // Los depósitos de proveedor no están suscritos en el contexto: se cargan
  // una vez al abrir la vista (snapshot, no live — igual que en Depósitos).
  const [provDepositos, setProvDepositos] = useState(null);
  useEffect(() => {
    let vivo = true;
    loadProveedorDepositos()
      .then((deps) => { if (vivo) setProvDepositos(deps); })
      .catch((err) => {
        console.error("loadProveedorDepositos error:", err);
        if (vivo) setProvDepositos([]);
      });
    return () => { vivo = false; };
  }, [loadProveedorDepositos]);

  const eventsById = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => { map[e.id] = e; });
    return map;
  }, [events]);

  const movimientos = useMemo(() => {
    const asientos = asientosDelLedger({
      payments,
      refunds,
      costos,
      depositos: [...(clienteDepositos || []), ...(provDepositos || [])],
      retiros,
    });
    return movimientosDeCaja(asientos)
      .map((m) => ({
        ...m,
        // Ancla de día calendario para el orden — la fecha en que el dinero
        // se movió; empates (mismo día) se desempatan por hora de captura y,
        // si aun así empatan (los asientos de un mismo depósito comparten
        // createdAt), por `orden`: el desenlace va después de recibirlo.
        _ms: calendarDayAnchor(m.fecha, calendarTimezone)?.getTime() || 0,
        _tie: m.createdAt?.seconds || 0,
        _orden: m.orden || 0,
      }))
      .sort((a, b) => b._ms - a._ms || b._tie - a._tie || b._orden - a._orden);
  }, [payments, refunds, costos, clienteDepositos, provDepositos, retiros, calendarTimezone]);

  const filtrados = useMemo(() => {
    if (clase === "todos") return movimientos;
    if (clase === "deposito") {
      return movimientos.filter((m) => m.tipo.startsWith("deposito_"));
    }
    // La comisión del planner es un gasto con etiqueta propia: cae en Gastos.
    if (clase === "gasto") {
      return movimientos.filter(
        (m) => m.tipo === "gasto" || m.tipo === "comision_planner" || m.tipo === "devolucion_proveedor"
      );
    }
    return movimientos.filter((m) => m.tipo === clase);
  }, [movimientos, clase]);
  const visibles = filtrados.slice(0, limite);

  const totales = useMemo(() => {
    let entradas = 0;
    let salidas = 0;
    filtrados.forEach((m) => {
      if (m.caja >= 0) entradas += m.caja;
      else salidas += -m.caja;
    });
    return { entradas, salidas, neto: entradas - salidas };
  }, [filtrados]);

  // proveedoresLoading: sin él, loadProveedorDepositos devuelve [] antes de
  // que el directorio cargue y el listado se pinta sin depósitos de
  // proveedor un instante.
  const loading =
    paymentsLoading ||
    refundsLoading ||
    costosLoading ||
    retirosLoading ||
    clienteDepositosLoading ||
    proveedoresLoading ||
    provDepositos === null;

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Cargando...</div>;
  }

  return (
    <div className="p-4">
      {/* Filtro + totales del listado filtrado */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex rounded-lg border border-zinc-300 dark:border-zinc-600 overflow-hidden">
          {CLASES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setClase(c.id); setLimite(PAGINA); }}
              className={`px-3 py-1.5 text-sm font-medium transition ${
                clase === c.id
                  ? "text-white"
                  : "bg-white dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-50"
              }`}
              style={clase === c.id ? { backgroundColor: ACCENT } : undefined}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-4 text-sm">
          <span className="text-zinc-500">
            Entradas:{" "}
            <span className="font-semibold text-emerald-600">{formatMXN(totales.entradas)}</span>
          </span>
          <span className="text-zinc-500">
            Salidas:{" "}
            <span className="font-semibold text-red-600">{formatMXN(totales.salidas)}</span>
          </span>
          <span className="text-zinc-500">
            Neto:{" "}
            <span className={`font-semibold ${totales.neto >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {formatMXN(totales.neto)}
            </span>
          </span>
        </div>
      </div>

      {visibles.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-zinc-900 dark:text-white font-semibold">
            Sin movimientos
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            Aquí aparece cada pago, reembolso, gasto, depósito y retiro, del
            más reciente al más viejo.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-700/60">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Fecha</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Tipo</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Concepto</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Método</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Evento</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Monto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {visibles.map((m) => {
                  const ev = m.eventId ? eventsById[m.eventId] : null;
                  const badge = BADGE[m.tipo] || { color: "zinc" };
                  const mostrarProveedor = !!m.proveedorNombre && m.tipo !== "comision_planner";
                  return (
                    <tr key={m.id}>
                      <td className="px-4 py-2.5 whitespace-nowrap text-zinc-500 w-32">
                        {formatCalendarDate(m.fecha, calendarTimezone, { style: "short" }) || "—"}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <Badge color={badge.color}>{ETIQUETAS_ASIENTO[m.tipo]}</Badge>
                        {m.parcial && (
                          <span className="ml-1.5 text-xs font-medium text-amber-600">Parcial</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {m.proveedorId ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/dashboard/proveedores/${m.proveedorId}`)}
                            className="text-sm hover:underline text-left"
                            style={{ color: ACCENT }}
                            title="Ver el proveedor"
                          >
                            {m.concepto}
                          </button>
                        ) : (
                          <div className="text-zinc-900 dark:text-white">{m.concepto}</div>
                        )}
                        {(m.detalle || m.parcial) && (
                          <div className="text-xs text-zinc-400">
                            {m.detalle}
                            {m.parcial && (
                              <>
                                {m.detalle ? " · " : ""}
                                Devolución parcial: se aplicaron {formatMXN(m.montoAplicado)}
                              </>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-zinc-600 dark:text-zinc-300">
                        {m.metodo || <span className="text-zinc-300 dark:text-zinc-600">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        {ev ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/dashboard/eventos/${ev.id}`)}
                            className="block max-w-[240px] truncate text-left text-sm hover:underline"
                            style={{ color: ACCENT }}
                            title={ev.nombre || "Sin nombre"}
                          >
                            {ev.nombre || "Sin nombre"}
                          </button>
                        ) : m.eventId ? (
                          <span className="text-xs text-zinc-400">Evento no encontrado</span>
                        ) : !mostrarProveedor ? (
                          <span className="text-xs text-zinc-400">General</span>
                        ) : null}
                        {/* Quién está del otro lado cuando hay proveedor: el
                            depósito o el gasto se entienden por el evento Y
                            por el proveedor, no solo por uno. La comisión del
                            planner ya lleva su nombre en el concepto. */}
                        {mostrarProveedor && (m.proveedorId ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/dashboard/proveedores/${m.proveedorId}`)}
                            className={`block max-w-[240px] truncate text-left hover:underline ${
                              ev ? "text-xs text-zinc-500 mt-0.5" : "text-sm"
                            }`}
                            style={ev ? undefined : { color: ACCENT }}
                            title={m.proveedorNombre}
                          >
                            {m.proveedorNombre}
                          </button>
                        ) : (
                          <span
                            className={`block max-w-[240px] truncate ${ev ? "text-xs text-zinc-500 mt-0.5" : "text-sm text-zinc-700 dark:text-zinc-300"}`}
                            title={m.proveedorNombre}
                          >
                            {m.proveedorNombre}
                          </span>
                        ))}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right font-medium whitespace-nowrap ${
                          m.caja >= 0 ? "text-emerald-600" : "text-red-600"
                        }`}
                      >
                        {m.caja >= 0 ? "+" : "−"}{formatMXN(Math.abs(m.caja))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-zinc-400">
              {visibles.length} de {filtrados.length} movimientos. Los costos
              por pagar viven en Por pagar y entran aquí al pagarse, con su
              fecha de pago.
            </p>
            {filtrados.length > limite && (
              <button
                type="button"
                onClick={() => setLimite((l) => l + PAGINA)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50"
              >
                Ver más
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CuentasMovimientos;
